import { Attachment } from '@/modules/Upload/types'
import ServiceTemplateComponents from '../components/Detail/Templates'
import {
    DNS_MANAGEMENT_TYPES,
    METADATA_KEYS,
    ServiceCreatableType,
} from '../constants'
import { Dayjs } from 'dayjs'
import {
    ContractDirection,
    ContractRole,
    ServiceConfigOption,
    ServiceTimes,
} from '@/modules/Contract/types'
import { RackEntry } from '../components/Detail/Templates/Server/Colocation/Information/RackUnit/MainServiceColocation'
export interface GetServiceOptionsRow {
    service_id: number
    service_name: string
    service_status: string
}

export type ServiceTemplates = keyof typeof ServiceTemplateComponents
export type AllSubServiceTemplates = {
    [K in keyof typeof ServiceTemplateComponents]: keyof (typeof ServiceTemplateComponents)[K]
}[keyof typeof ServiceTemplateComponents]

export interface Service {
    name?: string
    service_id: number
    service_name: string
    service_taxable: boolean
    service_type: string
    service_status: ServiceStatus
    is_poc: boolean
    // "POC tính phí" (POC list rows); optional until the API returns them
    is_paid?: boolean
    poc_price?: number
    service_template: ServiceTemplates
    product_group_code: string
    product_group_name: string
    servicable_type: string
    servicable_id: number
    customer_id: number
    contract_id: any
    contract_role?: ContractRole | null
    contract_direction?: ContractDirection | null
    product_id: number
    contact_id: number
    service_creatable_uuid: string
    service_creatable_type: ServiceCreatableType
    service_start_date: string | Dayjs | null
    service_stopped_date: any
    service_generate_date: string
    service_statement_date: string
    service_price_register: number
    service_price_recurring: any
    employee_create_id: number
    employee_create_name?: string | null
    employee_sale_tracking_ids: number[]
    created_at: string
    updated_at: string
    customer_name: string
    company_name: string
    service_time_id: number
    statement_startdate: string | null
    statement_enddate: string | null
    statement_range: any
    // present on the statement (đối soát) list: true when the service already has
    // a rollbackable statement snapshot (enables the rollback action).
    has_active_snapshot?: boolean
    // statement period a rollback would revert TO (null when no active snapshot)
    rollback_to_statement_startdate?: string | null
    rollback_to_statement_enddate?: string | null
    first_startdate: string | null
    first_enddate: string | null
    current_startdate: any
    current_enddate: any
    continue_startdate: string | null
    continue_enddate: string | null
    // statement startdate and endda
    renew_range: number
    // Số ngày công nợ: hạn thanh toán HĐ = ngày tạo HĐ + N. null khi chưa có
    // service_times → hiển thị mặc định 14.
    payment_term_days?: number | null
    employee_names: string[]
    domain_name?: string
    cs_followers?: string[]
    service_tax_percentage?: number
    service_pricing_model?: string
    //
    service_currency?: string
    service_exchange_rate?: number

    //
    customer_language?: string
}

export enum ServiceStatus {
    Active = 'active',
    WaitingConfirm = 'waiting-confirm',
    Trialing = 'trialing',
    Rejected = 'rejected',
    Resubmit = 'on-resubmit-information',
    Cancelled = 'cancelled',
    WaitingCreateService = 'waiting-create-service',
    PendingTerminated = 'pending-terminated',
} // Thêm các trạng thái khác nếu có

export type PeekSettings =
    | 'name'
    | 'commitment_value'
    | 'commitment_price_per_unit'
    | 'commitment_available'
    | 'commitment_time'
    | 'commitment_startdate'
    | 'commitment_enddate'
    | 'commitment_prepaid'
    | 'commitment_price_total'
    | 'statementable'

export interface ServiceInformationProps {
    service: Service
    view_setting?: boolean
    peek_settings?: PeekSettings[]
}

export interface ServiceSettingProps {
    service: Service
}

export interface ServiceNotificationProps {
    serviceID: number
    onClose: () => void
    productGroupName?: string
}
export interface ServiceReceiptProps {
    serviceID: number
}
export interface ServiceMonitoringProps {
    service: Service
}

export interface ServiceUpdate {
    service_update_id: number
    service_id: number
    service_update_type: string
    service_update_description: string
    service_update_metadata: any
    service_updater: { employee_name: string; employee_id: number }
    created_at: string
    updated_at: string
}

export interface ServiceInvoice {
    invoice_id: number
    customer_id: number
    customer_contact_id: number
    invoice_status: string
    invoice_type: string
    invoice_first_bill_of_service: boolean
    invoice_description: any
    invoice_note: string
    invoice_billing_scope: string
    invoice_renewable: any
    invoice_amount: number
    invoice_vat: number
    invoice_total: number
    invoice_commissions: number[]
    invoice_payment_at: any
    invoice_payment_method: string
    invoice_exporter_id: number
    invoice_exporter_code: any
    invoice_exporter_status: string
    invoice_duedate: string
    invoice_apply_startdate: string
    invoice_apply_enddate: string
    deleted_at: any
    created_at: string
    updated_at: string
    service_id: number
    invoice_items: any[]
}

export type DNSManagementType = (typeof DNS_MANAGEMENT_TYPES)[number]['value']
export type MetadataKeyType = (typeof METADATA_KEYS)[number]['value']

export interface ServiceNotification {
    notification_name: string
    service_system_information_id: number
    service_id: number
    dns_enable: boolean
    dns_managements: DnsManagement[]
    metadata: Metadata[]
    documentations: Documentation[]
    employee_id: number
    delivery_at: any
    created_at: string
    updated_at: string
    deleted_at: any
    employee_name: any
    attachments: Attachment[]
}

export interface DnsManagement {
    name: string
    type: string
    target: string
}

export interface Metadata {
    key: string
    value: string
}

export interface Documentation {
    link: string
    name: string
}

export interface ServiceConfigoption {
    service_configoption_id: number
    service_id: number
    name: string
    unit: string
    type: string
    brand_name: any
    model_name: any
    capacity_value: any
    capacity_unit: any
    priceoff: any
    resizable: any
    quota: any
    statementable: boolean
    commitment_value: number
    commitment_price_per_unit: number
    commitment_price_total: number
    commitment_lastest_startdate: any
    commitment_lastest_enddate: any
    commitment_lastest_metadata: any
    commitment_startdate: string
    commitment_enddate: string
    commitment_auto_renewable: any
    commitment_prepaid: boolean
    commitment_fulltimetable: any
    commitment_remaining: any
    commitment_available: any
    commitment_used: any
    warning_enabled: boolean
    warning_values: string
    deleted_at: any
    created_at: string
    updated_at: string
    service_tag: any
    commitment_time: number
    price_exceed_id: number
    method: {
        calc_amount_method: string
    }
    price_ranges: PriceRange[]
    metadata?: {
        racks?: RackEntry[]
    }
}

export interface PriceRange {
    currency_code?: string
    price_range_id?: number
    price_end: number
    price_start: number
    price_per_unit: number
}

export interface ServiceStatement {
    service_time_id: number
    statement_range?: number
    statement_startdate: string | Dayjs
    statement_enddate: string | Dayjs
    first_startdate: string | Dayjs
    first_enddate: string | Dayjs
    statement: string
}

export interface ServiceDate extends ServiceStatement {
    continue_enddate: string
    continue_startdate: string
    current_enddate: string
    current_startdate: string
    first_enddate: string
    first_startdate: string
    renew_range?: number
}

export interface ServiceCustomerMailer {
    customer_mailer_id: number
    customer_id: number
    customer_mailer_name: string
    mailer_type_ids: number[]
    mailer_receiver: string
    mailer_cc: string
    mailer_bcc: string
    service_ids: any[]
    sendable: boolean
    created_at: string
    updated_at: string
    deleted_at: any
}

export interface ServiceWithConfigoption extends Service {
    configoptions: ServiceConfigoption[]
}

export enum ServiceConfigoptionName {
    Local = 'local',
    Global = 'global',
    Bandwidth = 'bandwidth',
    Transcoding = 'transcoding',
    Transmuxing = 'transmuxing',
    Recording = 'recording',
    StorageOSS = '',
    StorageLFD = '',
    PeakBandwidth = 'peak-bandwidth',

    CDNTraffics = 'cdn_traffics',
    CDNRequests = 'cdn_requests',
    WAFTraffics = 'waf_traffics',
    WAFRequests = 'waf_requests',
    PeakCDNTraffics = 'peak_cdn_traffics',
    PeakWAFTraffics = 'peak_waf_traffics',
    PeakCDNRequests = 'peak_cdn_requests',
    PeakWAFRequests = 'peak_waf_requests',
    DNSQueries = 'dns_queries',

    OriginShieldTraffics = 'origin_shield_traffics',
    OriginShieldRequests = 'origin_shield_requests',
    DnsRequests = 'dns_requests',
    Domains = 'domains',

    Storage = 'storage',
    Website = 'website',
    MySQL2 = 'my-sql',
    MySQL = 'mysql',

    Memory = 'memory',
    VCORE = 'vcore',
    CPU = 'cpu',
    SSD = 'ssd',

    EmailAccount = 'account',
    EmailAccount2 = 'mail-account',
    EmailStorage = 'storage',
    EmailDomain = 'addon-domain',
    EmailAttachment = 'email-attachment',

    IP = 'ip',
    Processor = 'processor',
    Server = 'server',
    Disk = 'disk',

    ColocationU = 'colocation-u',
    U = 'u',
    Power = 'power',
}

export interface ServiceCancelProps {
    service_stopped_date: string | null
    service_reason_cancel: string
}

export interface Provider {
    id: string
    name: string
    description: string
    scope: string
    type: string
    version: string
    zones: Zone[]
    creatable_type: string
}

export interface Zone {
    id: string
    name: string
    description: string
    type: string
}

export interface ServiceInformation {
    service_information_id: number
    service_id: number
    name: string
    value: string
    price: number
    unit: any
    created_at: string
    updated_at: string
    statemented: boolean
    statemente_key: string
    description: string
    warehouse_item_service_tag: string
    warehouse_model_name: string
    service_information_type: string
    service_addon_id: number
    warehouse_brand_name: string
    warehouse_capacity_value: number
    warehouse_capacity_unit: string
    metadata: any
    discount: number
}

export interface AnalyticParams {
    gte?: string
    lte?: string
    type: string
    interval?: string
}

export interface MonitoringQueries {
    gte: string
    lte: string
    range:
        | string
        | '7d'
        | '30d'
        | 'this-month'
        | 'last-month'
        | 'customize-range'
}
export interface ServiceMailerType {
    metadata: Metadata
    task_bind: string
    task_completed_at: string
    task_created_at: string
    task_description: string
    task_id: number
    task_jid: string
    task_name: string
    task_referrer_id: number
    task_referrer_jid: string
    task_referrer_type: string
    task_scope: string
    task_status: string
    task_updated_at: string
    task_uuid: string
}

export interface Metadata {
    metadata: Metadata2
    sender: Sender2
    subject: string
}

export interface Metadata2 {
    attachments: Attachment[]
    customer: Customer
    mailer: Mailer
    notification: string
    sender: Sender
    service: Service
    service_name: string
}

export interface Customer {
    customer_email: string
    customer_id: number
    customer_name: string
}

export interface Mailer {
    customer_mailer_name: string
    mailer_bcc: string
    mailer_cc: string
    mailer_receiver: string
}

export interface Sender {
    employee_email: string
    employee_id: number
    employee_name: string
}

export interface Service {
    service_id: number
    service_name: string
}

export interface Sender2 {
    employee_email: string
    employee_id: number
    employee_name: string
}

export interface ServiceAlert {
    id: string
    service_configoption_id: number
    name: string
    unit: string
    threshold_type: string
    warning_threshold: number
    critical_threshold: number
    commitment_value: number
    enabled: boolean
    created_at: string
    updated_at: string
}

export interface InternalTicketWorkflow {
    ticket_id: number
    ticket_status: string
    ticket_cs_assignees: any[]
    ticket_technical_assignees: number[]
    ticket_employee_assignees: any[]
    ticket_deadline_at: any
    ticket_resolved_at: string
    created_at: string
    updated_at: string
    ticket_type_title: string
    ticket_type_key: string
    ticket_workflow_steps: TicketWorkflowStep[]
}

export enum InternalTicketWorkflowStatus {
    Todo = 'todo',
    Doing = 'doing',
    Blocked = 'blocked',
    Done = 'done',
}

export enum InternalTicketWorkflowStepName {
    CSKHConfirm = 'cskh_verification',
    TechnicalCreateService = 'technical_create_service',
    CSKHVerifyCreateService = 'cskh_verify_create_service',
    FinalDone = 'final_done',
    CSKHConfirmCancel = 'cskh_confirm_cancel',
    TechnicalCancelService = 'technical_deactivate_service',
    TechnicalSuspendService = 'technical_suspend_service',
    CSKHConfirmTaxPercentageUpdate = 'cs_confirm_tax_percentage_update',
    FinanceApproveTaxPercentageUpdate = 'finance_approve_tax_percentage_update',
    // POC workflow steps
    CSKHXacNhan = 'cskh_xac_nhan',
    TechKhoiTaoTrenDC = 'technical_create_service',
    CSKHKiemTraHoanTat = 'cskh_kiem_tra_hoan_tat',
}

export interface TicketWorkflowStep {
    approved_at: any
    approved_by: any
    created_at: string
    filled_data: FilledData
    required_fields: string[]
    status: InternalTicketWorkflowStatus
    step_id: number
    step_name: InternalTicketWorkflowStepName
    step_order: number
    updated_at: string
    approver_name: string
}

export interface FilledData {
    hash?: string
    note?: string
    service_tax_percentage?: number
    [key: string]: any
}
export interface ServiceVNISProvider {
    customerId: string
    ctime: number
    email: string
    isActive: boolean
    loginDate: number
    hasEmailVerified: boolean
    firstName: string
    lastName: string
    organizationId: string
    organizationName: string
    organizationState: string
    organizationVersion: string
    parentOrganizationName: string
}

export interface ServicePrice {
    id: number
    service_id: number
    service_name?: string
    price_type: 'recurring' | 'register'
    price: number
    effective_from: string
    effective_to: string | null
    source: string
    source_id: number | null
    description: string
    created_by: number | null
    created_at: string
    updated_at: string
}

export interface CreateServicePricePayload {
    price_type: 'recurring' | 'register'
    price: number
    effective_from: string
    effective_to?: string | null
    source?: string
    source_id?: number | null
    description?: string
}

export interface UpdateServicePricePayload {
    price?: number
    effective_from?: string
    effective_to?: string | null
    description?: string
}

export interface ServiceVatRate {
    id: number
    source: 'product_group' | 'service'
    product_group_code?: string
    service_id?: number
    tax_rate: number
    effective_from: string
    effective_to: string | null
    description: string
    created_at: string
    updated_at: string
}

export interface ConfigOptionPriceHistory {
    id: number
    service_configoption_id: number
    commitment_value: number
    commitment_price_per_unit: number
    effective_from: string
    effective_to: string | null
    source: string
    description: string
    created_at: string
    updated_at: string
}

export interface CreateConfigOptionPricePayload {
    commitment_value: number
    commitment_price_per_unit: number
    effective_from: string
    effective_to?: string | null
    source?: string
    description?: string
}

export interface UpdateConfigOptionPricePayload {
    commitment_value?: number
    commitment_price_per_unit?: number
    effective_from?: string
    effective_to?: string | null
    description?: string
}

export interface PriceChangeRequest {
    id: number
    service_id: number
    service_name: string
    product_group_code: string
    source_type: string
    source_id: number
    old_price: number
    new_price: number
    price_type: 'recurring' | 'register'
    effective_from: string | null
    status: 'pending' | 'applied' | 'rejected'
    reviewed_by: number | null
    reviewed_at: string | null
    note: string
    created_at: string
    updated_at: string
}

export interface AddonsFormValues {
    addons: {
        service_parent_name: string
        product_id: number
        service_parent_id: number
        service_addons_template: string
        service_configoptions: Partial<ServiceConfigOption>[]
        service_price_register: number
        service_times: ServiceTimes
        service_id: number
        service_name: string
        employee_sale_tracking_ids: number[]
        customer_id: number
    }
}
